/** How one listing row takes part in the run's joins (plan 2026-10-11
 *  product-sync joins §7, "Row state"): the chips under "Under this policy".
 *
 *  - a primary shows `+N joined` (the tooltip names the members);
 *  - a member shows `joins ‹A›` with a detach ×;
 *  - a product whose key is an additional ID of another tour shows
 *    `additional ID of ‹tour› · not built`: the re-sync guard (D2) leaves it
 *    out of the run, visible here before launch instead of only in the task
 *    result. With the integration's `product_split_off` feature it offers
 *    "Split off and build its own tour…"; once split off, the chip reads
 *    `splits off ‹T›` with an undo ×;
 *  - a product the start refused (`product_join_invalid`) shows `refused`, the
 *    tooltip quoting the server's reasons.
 */
import CancelIcon from '@mui/icons-material/Cancel';
import { Button, Chip, Stack, Tooltip } from '@mui/material';

import type { ProductJoinProblem, ProductMapping } from '@/api/sync';

import type { KeyGuard } from './productJoins';

export interface JoinRowInfo {
  role: 'primary' | 'member' | null;
  /** A primary's members, in join order. */
  members: { id: string; name: string }[];
  /** A member's primary. */
  primary: { id: string; name: string } | null;
  guard: KeyGuard;
  /** The start's refusal reasons naming this product. */
  problems: ProductJoinProblem[];
}

export function tourName(t: ProductMapping['tour']): string {
  return t.name || `#${t.id}`;
}

/** The distinct tours of `rows`, in order. */
export function toursOf(rows: ProductMapping[]): ProductMapping['tour'][] {
  const seen = new Map<number, ProductMapping['tour']>();
  for (const m of rows) if (!seen.has(m.tour.id)) seen.set(m.tour.id, m.tour);
  return [...seen.values()];
}

export function JoinRowChips({
  info,
  onDetach,
  onSplitOff,
  onUndoSplitOff,
}: {
  info: JoinRowInfo;
  /** Detach this member from its group (it is built on its own again). */
  onDetach?: () => void;
  /** Open the split-off confirmation; absent when it cannot be offered. */
  onSplitOff?: () => void;
  onUndoSplitOff?: () => void;
}) {
  const { role, members, primary, guard, problems } = info;
  const elsewhere = toursOf(guard.elsewhere);
  // A member is not built, so the guard says nothing about it: its own IDs
  // are what the Combine dialog asked about.
  const showGuard = role !== 'member' && guard.verdict !== 'free';
  if (role === null && !showGuard && problems.length === 0) return null;
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
      {role === 'primary' && (
        <Tooltip
          title={`Creates one tour; joined into it as additional IDs: ${members.map((m) => `${m.name} (${m.id})`).join(', ')}`}
        >
          <Chip size="small" color="primary" label={`+${members.length} joined`} />
        </Tooltip>
      )}
      {role === 'member' && primary && (
        <Tooltip
          title={`Not built: its IDs become additional IDs of the tour ${primary.name} creates.`}
        >
          <Chip
            size="small"
            color="primary"
            variant="outlined"
            label={`joins ${primary.name}`}
            onDelete={onDetach}
            deleteIcon={<CancelIcon titleAccess={`Detach from ${primary.name}`} />}
          />
        </Tooltip>
      )}
      {showGuard && guard.verdict === 'left_out' && (
        <>
          <Tooltip
            title={`Its tour key is an additional ID of ${elsewhere.map(tourName).join(', ')}. Syncing it would overwrite that tour with this product's content, so the run leaves it out.`}
          >
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label={`additional ID of ${elsewhere.map(tourName).join(', ')} · not built`}
            />
          </Tooltip>
          {onSplitOff && (
            <Button size="small" onClick={onSplitOff} sx={{ px: 0.5, minWidth: 0 }}>
              Split off and build its own tour…
            </Button>
          )}
        </>
      )}
      {showGuard && guard.verdict === 'split_off' && elsewhere[0] && (
        <Tooltip
          title={`${tourName(elsewhere[0])} stops using this ID; the run builds the product as its own tour.`}
        >
          <Chip
            size="small"
            color="info"
            label={`splits off ${tourName(elsewhere[0])}`}
            onDelete={onUndoSplitOff}
            deleteIcon={<CancelIcon titleAccess="Undo split-off" />}
          />
        </Tooltip>
      )}
      {problems.length > 0 && (
        <Tooltip title={problems.map((p) => p.detail).join(' ')}>
          <Chip size="small" color="error" label="refused" />
        </Tooltip>
      )}
    </Stack>
  );
}
